<template>
  <div class="column full-height no-wrap">
    <!-- Pitch Grid Canvas -->
    <div
      ref="containerRef"
      class="canvas-container col-grow relative-position overflow-hidden bg-grey-2"
    >
      <!-- TOP UI BAR (Undo, Batter Edit, Next Up Menu) -->
      <div
        class="absolute-top row items-start justify-between q-pa-sm full-width"
        style="z-index: 10; pointer-events: none"
      >
        <!-- LEFT: Floating Undo Button -->
        <div style="pointer-events: auto">
          <q-btn
            round
            dense
            color="white"
            text-color="primary"
            icon="undo"
            @click="handleUndo"
            :disable="(!activeEditPitch && !undoIsRunnerAdvance) || isUndoing"
            :loading="isUndoing"
            class="shadow-2"
          >
            <q-menu anchor="bottom left" self="top left" no-parent-event>
              <q-card class="q-pa-sm" style="min-width: 200px">
                <div class="text-caption q-mb-sm">
                  <!-- NEW: Check if the top of the stack is a runner action -->
                  <template v-if="undoIsRunnerAdvance">
                    Undo the last runner advance (Pickoff/Steal)? This can't be undone.
                  </template>
                  <template v-else-if="undoHasLinkedPlay">
                    <q-icon name="warning" color="negative" size="xs" class="q-mr-xs" />
                    This pitch has a linked play or runner advance — undoing it will delete those
                    too. This can't be undone.
                  </template>
                  <template v-else> Undo this pitch? This can't be undone. </template>
                </div>
                <div class="row justify-end q-gutter-xs">
                  <q-btn flat dense label="Cancel" color="grey-7" v-close-popup />
                  <q-btn
                    flat
                    dense
                    label="Undo"
                    color="negative"
                    v-close-popup
                    @click="handleUndo"
                  />
                </div>
              </q-card>
            </q-menu>
            <HelpHint text="Undo the last pitch." />
          </q-btn>
        </div>

        <!-- RIGHT: Roster Info & Edit Toggle -->
        <div class="row items-start justify-end q-gutter-x-sm col">
          <!-- Edit/Info Box (Click-through when not editing) -->
          <div
            class="row items-center bg-white rounded-borders q-pa-xs transition-style"
            :class="showEditUniform ? 'shadow-2' : 'shadow-1'"
            :style="{
              pointerEvents: showEditUniform ? 'auto' : 'none',
              opacity: showEditUniform ? 1 : 0.85,
            }"
          >
            <!-- EDIT MODE -->
            <div v-if="showEditUniform" class="row items-center q-gutter-x-sm">
              <q-input
                v-model.number="editUniformNumber"
                type="number"
                dense
                outlined
                label="Uniform #"
                style="width: 80px"
                @focus="handleFocus"
                @blur="autoSaveRoster"
                @keyup.enter="toggleEditMode"
              />
              <q-btn-toggle
                v-model="editBats"
                dense
                no-caps
                unelevated
                toggle-color="primary"
                color="grey-2"
                text-color="grey-9"
                :options="[
                  { label: 'L', value: 'L' },
                  { label: 'R', value: 'R' },
                  { label: 'S', value: 'S' },
                ]"
                @update:model-value="autoSaveRoster"
              />
            </div>

            <!-- READ-ONLY INFO (Allows clicks to pass through to canvas) -->
            <div v-else class="text-weight-bold text-primary q-px-sm text-caption">
              #{{ editUniformNumber ?? '-' }} ({{ editBats }}) | Nxt: #{{
                nextBatterPreview?.player.uniformNumber ?? '-'
              }}
            </div>
          </div>

          <!-- Edit Toggle Button -->
          <div style="pointer-events: auto">
            <q-btn
              round
              dense
              color="white"
              text-color="primary"
              :icon="showEditUniform ? 'close' : 'badge'"
              class="shadow-2"
              @click="toggleEditMode"
            >
              <HelpHint text="Edit Batter Info" top="-2px" left="-3px" />
            </q-btn>
          </div>
        </div>
      </div>

      <!-- BOTTOM UI BAR (Pickoffs, Steals, Perspective) -->
      <div
        class="absolute-bottom row items-end justify-between q-pa-sm full-width"
        style="z-index: 10; pointer-events: none"
      >
        <!-- LEFT: Runner Actions (Visible only if runners on base) -->
        <div v-if="hasRunners" class="row q-gutter-sm" style="pointer-events: auto">
          <q-btn
            round
            color="white"
            text-color="primary"
            class="shadow-2"
            @click="openActionSheet('Pickoff')"
          >
            <RunnerActionIcon direction="pickoff" />
          </q-btn>
          <q-btn
            round
            color="white"
            text-color="primary"
            class="shadow-2"
            @click="openActionSheet('Steal')"
          >
            <RunnerActionIcon direction="steal" />
          </q-btn>
        </div>
        <div v-else></div>

        <!-- RIGHT: Perspective Toggle -->
        <div style="pointer-events: auto">
          <q-btn-toggle
            v-model="isPitcherPerspective"
            rounded
            unelevated
            toggle-color="primary"
            color="white"
            text-color="grey-8"
            :options="[
              { label: 'Umpire View', value: false, icon: 'visibility' },
              { label: 'Pitcher View', value: true, icon: 'swap_vert' },
            ]"
            class="shadow-3 text-weight-bold"
            size="sm"
          />
        </div>
      </div>

      <v-stage
        class="absolute-top-left"
        :config="{
          ...stageConfig,
          onClick: handleGridTap,
          onTap: handleGridTap,
          listening: !isTappingGrid,
        }"
      >
        <v-layer :config="{ opacity: isTappingGrid ? 0.6 : 1 }">
          <!-- Draw Strike Zone -->
          <v-rect :config="strikeZoneConfig" />
          <v-line :config="gridLineH1" />
          <v-line :config="gridLineH2" />
          <v-line :config="gridLineV1" />
          <v-line :config="gridLineV2" />

          <!-- Batter Handedness Indicators -->
          <v-text :config="leftBatterBoxConfig" />
          <v-text :config="rightBatterBoxConfig" />

          <!-- Plotted Pitches -->
          <v-group
            v-for="pitch in plottedPitches"
            :key="pitch.id"
            :config="{
              x: pitch.x,
              y: pitch.y,
              draggable: true,
              id: String(pitch.id),
              onDragend: handleStageDragEnd,
              onClick: () => selectPitch(pitch.id),
              onTap: () => selectPitch(pitch.id),
            }"
          >
            <v-circle
              :config="{
                radius: 12,
                fill: pitch.color,
                stroke: isSelected(pitch.id) ? '#FFC107' : '#2c3e50',
                strokeWidth: isSelected(pitch.id) ? 3 : 1.5,
              }"
            />
            <v-text
              :config="{
                text: pitch.number.toString(),
                fontSize: 12,
                fill: 'white',
                fontStyle: 'bold',
                align: 'center',
                verticalAlign: 'middle',
                width: 24,
                height: 24,
                x: -12,
                y: -12,
              }"
            />
          </v-group>
        </v-layer>
      </v-stage>
    </div>

    <!-- Control Cards Area -->
    <q-card flat class="shrink shadow-up-2">
      <q-card-section class="q-py-none">
        <!-- Untracked pitch chips — only shows if any exist for this at-bat -->
        <div
          v-if="untrackedPitches.length > 0"
          class="row q-gutter-xs q-px-none q-pb-xs items-center wrap"
        >
          <div class="text-caption text-grey-7">No location:</div>
          <q-chip
            v-for="p in untrackedPitches"
            :key="p.id"
            clickable
            dense
            :color="getChipColor(p.outcome, isSelected(p.id))"
            :text-color="getChipTextColor(p.outcome, isSelected(p.id))"
            @click="selectPitch(p.id)"
          >
            #{{ p.number }} {{ p.outcome }}
          </q-chip>
        </div>

        <!-- Primary Categories -->
        <div
          class="row q-gutter-x-xs q-mb-sm no-wrap items-center"
          v-if="authStore.currentUser?.settings?.enablePitchCategories"
        >
          <q-btn
            round
            dense
            flat
            :icon="quickLogMode ? 'location_off' : 'location_on'"
            :color="quickLogMode ? 'grey-6' : 'primary'"
            size="sm"
            @click="quickLogMode = !quickLogMode"
          >
            <HelpHint
              :text="
                quickLogMode
                  ? 'Quick log is on — taps record the outcome without a pitch location.'
                  : 'Tap the strike zone first, then choose the outcome here.'
              "
            />
          </q-btn>

          <q-btn
            v-for="cat in settingsStore.primaryPitchCategories?.filter((c) => c.visible !== false)"
            :key="cat.value"
            :label="cat.label"
            :color="cat.color"
            :text-color="cat.textColor"
            class="col text-weight-bold"
            unelevated
            padding="xs"
            :loading="quickLogMode && isQuickLogging"
            :disable="quickLogMode && isQuickLogging"
            :outline="
              primaryCategory !== cat.value ||
              settingsStore.pitchAdvancedOptionsValues.includes(pitchOutcome || '')
            "
            @click="quickLogMode ? handleQuickLog(cat.value) : handlePrimaryCategory(cat.value)"
          />
          <q-btn
            icon="more_horiz"
            color="grey-8"
            class="col text-weight-bold"
            size="sm"
            padding="xs"
            unelevated
            :outline="!settingsStore.pitchAdvancedOptionsValues.includes(pitchOutcome || '')"
            @click="advancedDrawerOpen = true"
          >
            <HelpHint :text="otherLabel" />
          </q-btn>
        </div>

        <!-- Sub Categories (Data Driven) -->
        <div
          class="row q-gutter-x-xs q-mb-sm no-wrap"
          v-if="
            authStore.currentUser?.settings?.enablePitchCategories &&
            primaryCategory &&
            settingsStore.subPitchCategories?.[primaryCategory]?.filter((s) => s.visible !== false)
              .length
          "
        >
          <q-btn
            v-for="sub in settingsStore.subPitchCategories[primaryCategory]!.filter(
              (s) => s.visible !== false,
            )"
            :key="sub.value"
            :label="sub.label"
            :color="sub.color"
            :text-color="sub.textColor"
            class="col text-weight-bold"
            unelevated
            padding="xs"
            :outline="pitchOutcome !== sub.value"
            @click="handleOutcome(sub.value)"
          />
        </div>
        <!-- Spacer placeholder to keep layout heights consistent when nothing is selected -->
        <div v-else class="row q-gutter-x-xs q-mb-sm no-wrap" style="min-height: 25px"></div>

        <!-- Pitch Type & Tools -->
        <div
          class="row q-gutter-x-xs q-mb-sm no-wrap"
          v-if="authStore.currentUser?.settings?.enablePitchTypes"
        >
          <q-btn
            v-for="sub in settingsStore.pitchTypes.filter((s) => s.visible !== false)"
            :key="sub.value"
            :label="sub.label"
            :color="sub.color"
            :text-color="sub.textColor"
            class="col text-weight-bold"
            unelevated
            padding="xs"
            :outline="pitchType !== sub.value"
            @click="handlePitchType(sub.value)"
          />
        </div>

        <!-- Pitch Quality -->
        <div
          class="row q-gutter-x-xs q-mb-sm no-wrap"
          v-if="authStore.currentUser?.settings?.enablePitchQuality"
        >
          <q-btn
            v-for="q in settingsStore.primaryPitchQualityStates"
            :key="q.value"
            :label="q.label"
            :color="pitchQuality === q.value ? 'warning' : 'grey-2'"
            :text-color="pitchQuality === q.value ? 'white' : 'black'"
            class="col text-weight-bold"
            unelevated
            padding="xs"
            @click="handlePitchQuality(q.value)"
          />
        </div>
      </q-card-section>
    </q-card>

    <!-- Advanced Drawer -->
    <MenuSelect
      v-model="advancedDrawerOpen"
      title="Other Pitch Outcomes"
      :options="settingsStore.pitchAdvancedOptions"
      @select="handleAdvancedOption"
      @hide="syncTogglesToLastPitch"
    />

    <!-- FOCUSED PICKOFF / STEAL SETUP WIZARD -->
    <q-dialog v-model="showRunnerActionSheet" position="bottom">
      <q-card class="bg-white q-pa-md full-width" style="border-radius: 16px 16px 0 0">
        <div class="row justify-between items-center q-mb-md">
          <div class="text-h6 text-weight-bold text-primary text-uppercase">
            {{ runnerActionType }} Attempt
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>

        <!-- STEP 0: TARGET RUNNER (Only shows if > 1 runner is on base) -->
        <div v-if="activeRunners.length > 1" class="q-mb-md">
          <div class="text-caption text-weight-bold text-grey-7 q-mb-xs">TARGET RUNNER</div>
          <q-btn-toggle
            v-model="targetRunnerId"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="activeRunners"
          />
        </div>

        <div v-else-if="activeRunners.length === 1" class="q-mb-md text-subtitle2 text-grey-8">
          Target: <span class="text-weight-bold text-primary">{{ activeRunners[0]!.label }}</span>
        </div>

        <!-- STEP 1: INITIATOR -->
        <div class="q-mb-md">
          <div class="text-caption text-weight-bold text-grey-7 q-mb-xs">THROWN BY</div>
          <q-btn-toggle
            v-model="draftInitiator"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="[
              { label: 'Pitcher (1)', value: 1 },
              { label: 'Catcher (2)', value: 2 },
            ]"
          />
        </div>

        <!-- STEP 2: RECEIVER -->
        <div class="q-mb-md">
          <div class="text-caption text-weight-bold text-grey-7 q-mb-xs">RECEIVED BY</div>
          <q-btn-toggle
            v-model="draftReceiver"
            spread
            no-caps
            unelevated
            toggle-color="primary"
            color="grey-2"
            text-color="grey-9"
            :options="[
              { label: '1B (3)', value: 3 },
              { label: '2B (4)', value: 4 },
              { label: '3B (5)', value: 5 },
              { label: 'SS (6)', value: 6 },
            ]"
          />
        </div>

        <q-btn
          label="Continue to Result"
          color="primary"
          class="full-width q-mt-sm"
          unelevated
          size="lg"
          icon-right="arrow_forward"
          :disable="!targetRunnerId || !draftInitiator || !draftReceiver"
          @click="buildSequenceAndContinue"
        />
      </q-card>
    </q-dialog>

    <!-- SHEET: RUNNER WIZARD RESOLUTION (Add this!) -->
    <q-dialog v-model="showRunnerSheet" position="bottom">
      <q-card
        class="bg-white q-pa-md"
        style="width: 100%; border-top-left-radius: 16px; border-top-right-radius: 16px"
      >
        <div class="row justify-between items-center q-mb-md">
          <div class="text-h6 text-weight-bold text-primary">
            Runner #{{ selectedRunnerUniform }}
            <span class="text-grey-6 text-subtitle1"
              >on {{ draftStartBase === 'B' ? 'Home' : draftStartBase + 'B' }}</span
            >
          </div>
          <q-btn flat round dense icon="close" v-close-popup />
        </div>

        <!-- STEP 1: DESTINATION -->
        <div>
          <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">MOVE TO</div>
          <div class="row q-gutter-sm q-mb-md">
            <q-btn
              v-for="base in availableBases"
              :key="base"
              :label="
                base === draftStartBase
                  ? 'Stay (' + base + 'B)'
                  : base === 'H'
                    ? 'Home'
                    : base + 'B'
              "
              :color="draftTargetBase === base ? 'primary' : 'grey-3'"
              :text-color="draftTargetBase === base ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setTargetBase(base)"
            />
          </div>
        </div>

        <!-- STEP 2: RESULT -->
        <div>
          <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">RESULT</div>
          <div class="row q-gutter-sm q-mb-md">
            <q-btn
              :label="draftTargetBase === 'H' ? 'Score' : 'Safe'"
              :color="draftIsOut === false ? 'positive' : 'grey-3'"
              :text-color="draftIsOut === false ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setResult(false)"
            />
            <q-btn
              label="Out"
              :color="draftIsOut === true ? 'negative' : 'grey-3'"
              :text-color="draftIsOut === true ? 'white' : 'black'"
              unelevated
              class="col-grow text-weight-bold"
              @click="setResult(true)"
            />
          </div>
        </div>

        <!-- STEP 3: REASON & RESOLUTION -->
        <div>
          <!-- HIDE these elements for Pickoffs -->
          <template v-if="runnerActionType !== 'Pickoff'">
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">
              REASON
              <span v-if="!reasonTouched" class="text-grey-5 text-weight-regular">
                (defaulting to Steal)</span
              >
            </div>
            <div class="row q-gutter-xs q-mb-md wrap justify-center">
              <q-btn
                v-for="r in reasonOptions"
                :key="r.value"
                :label="r.label"
                :color="draftReason === r.value ? 'primary' : 'grey-2'"
                :text-color="draftReason === r.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="col-5 text-weight-medium"
                @click="selectReason(r.value)"
              />
            </div>

            <div v-if="draftReason === 'DEFENSE'" class="q-mb-md">
              <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">DEFENSE TYPE</div>
              <div class="row q-gutter-xs wrap justify-center">
                <q-btn
                  v-for="s in defenseSubReasonOptions"
                  :key="s.value"
                  :label="s.label"
                  :color="draftDefenseSubReason === s.value ? 'primary' : 'grey-2'"
                  :text-color="draftDefenseSubReason === s.value ? 'white' : 'black'"
                  unelevated
                  size="sm"
                  class="col-5 text-weight-medium"
                  @click="selectDefenseSubReason(s.value)"
                />
              </div>
            </div>

            <div class="q-mb-md">
              <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">JUMP</div>
              <div class="row q-gutter-xs wrap justify-center">
                <q-btn
                  v-for="j in jumpOptions"
                  :key="j.value"
                  :label="j.label"
                  :color="draftJump === j.value ? 'primary' : 'grey-2'"
                  :text-color="draftJump === j.value ? 'white' : 'black'"
                  unelevated
                  size="sm"
                  class="col-5 text-weight-medium"
                  @click="selectJump(j.value)"
                />
              </div>
            </div>

            <div class="q-mb-md">
              <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">DECISION</div>
              <div class="row q-gutter-xs wrap justify-center">
                <q-btn
                  v-for="d in decisionOptions_runner"
                  :key="d.value"
                  :label="d.label"
                  :color="draftDecision === d.value ? 'primary' : 'grey-2'"
                  :text-color="draftDecision === d.value ? 'white' : 'black'"
                  unelevated
                  size="sm"
                  class="col-5 text-weight-medium"
                  @click="selectDecision(d.value)"
                />
              </div>
            </div>
          </template>
          <!-- SHARED FIELDERS INVOLVED INDICATOR (Inside Runner Sheet) -->
          <div
            v-if="activeSequence.length > 0"
            class="q-mb-md bg-grey-1 rounded-borders q-pa-sm text-center"
          >
            <div class="text-caption text-weight-bold text-grey-7 q-mb-sm">
              FIELDERS INVOLVED (Tap to edit)
            </div>
            <div class="column items-center">
              <div class="row justify-center items-center q-mb-xs">
                <template v-for="(item, idx) in activeSequence" :key="idx">
                  <div
                    class="q-px-sm q-py-xs bg-white border-primary text-primary text-weight-bold rounded-borders shadow-1 cursor-pointer"
                    @click="openSequenceEdit(idx)"
                  >
                    {{ getPositionName(item.position) }}
                    <q-icon
                      v-if="item.isError"
                      name="warning"
                      color="negative"
                      size="xs"
                      class="q-ml-xs"
                    />
                  </div>
                  <q-icon
                    v-if="idx < activeSequence.length - 1"
                    name="arrow_forward"
                    color="grey-6"
                    class="q-mx-sm"
                  />
                </template>
              </div>
            </div>
          </div>
          <q-btn
            label="Save Runner"
            color="primary"
            class="full-width q-mt-sm"
            unelevated
            size="lg"
            :loading="isSavingRunner"
            :disable="!draftReason || !draftTargetBase || draftIsOut === null || isSavingRunner"
            @click="saveRunner"
          />
        </div>
      </q-card>
    </q-dialog>

    <!-- CONTEXTUAL FIELDER DEVELOPMENT HUD (Mechanics & IQ) -->
    <q-dialog v-model="showEditFielderSheet" position="top" @hide="onFielderSheetHide">
      <q-card class="bg-white" style="width: 100%; border-radius: 0 0 16px 16px">
        <!-- Header -->
        <q-toolbar class="bg-primary text-white">
          <q-btn
            flat
            round
            dense
            icon="chevron_left"
            :disable="(editSequenceIndex || 0) <= 0"
            @click="navigateSequence(-1)"
          />
          <q-toolbar-title class="text-subtitle1 text-weight-bold text-center">
            {{ editItemName }} <span class="text-white opacity-80">#{{ editItemUniform }}</span>
          </q-toolbar-title>
          <q-btn
            flat
            round
            dense
            icon="chevron_right"
            :disable="(editSequenceIndex || 0) >= activeSequence.length - 1"
            @click="navigateSequence(1)"
          />
          <q-btn flat round dense icon="close" v-close-popup />
        </q-toolbar>

        <q-card-section class="q-py-sm scroll hide-scrollbar" style="max-height: 80vh">
          <!-- 1. ROLE -->
          <div v-if="!computedRoleHidden">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Player Role</div>
            <q-btn-toggle
              v-model="editRoleState"
              spread
              no-caps
              unelevated
              toggle-color="primary"
              color="grey-2"
              text-color="grey-9"
              :options="settingsStore.primaryFielderRoles"
              @update:model-value="onEditChange"
            />
          </div>

          <div v-if="!computedDifficultyHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Difficulty</div>
            <q-btn-toggle
              v-model="editDifficultyState"
              spread
              no-caps
              unelevated
              toggle-color="warning"
              color="grey-2"
              text-color="grey-9"
              :options="settingsStore.playQualityOptions"
              @update:model-value="onEditChange"
              class="full-width text-weight-medium"
            />
          </div>

          <!-- 2. APPROACH -->
          <div v-if="!computedApproachHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Approach</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in approachOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editApproachState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editApproachState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleApproach(opt.value)"
              />
            </div>
          </div>

          <!-- 3. EXECUTION -->
          <div v-if="!computedExecutionHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Execution</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in executionOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editExecutionState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editExecutionState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleExecution(opt.value)"
              />
            </div>
          </div>

          <!-- 4. THROW -->
          <div v-if="!computedThrowHidden" class="q-mt-sm">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Throw</div>
            <div class="row q-gutter-xs wrap">
              <q-btn
                v-for="opt in throwOptions"
                :key="opt.value"
                :label="opt.label"
                :color="editThrowState === opt.value ? 'primary' : 'grey-2'"
                :text-color="editThrowState === opt.value ? 'white' : 'black'"
                unelevated
                size="sm"
                class="text-weight-bold"
                @click="onEditHandleThrow(opt.value)"
              />
            </div>
          </div>

          <!-- 5. ERRORS & DECISION -->
          <div class="q-mt-md">
            <div class="text-caption text-weight-bold text-grey-8 q-mb-xs">Errors & Decision</div>
            <div class="row q-gutter-sm items-center">
              <q-select
                v-model="editErrorState"
                :options="settingsStore.primaryPlayErrorStates"
                label="Scorebook Error"
                dense
                outlined
                emit-value
                map-options
                :class="decisionHidden ? 'col-12' : 'col-12 col-sm-5'"
                @update:model-value="onEditChange"
              />
              <q-select
                v-if="!decisionHidden"
                v-model="editDecisionState"
                :options="decisionOptions"
                label="Decision"
                dense
                outlined
                emit-value
                map-options
                class="col-12 col-sm-6 col-grow"
                @update:model-value="onEditHandleDecision"
              />
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right" class="bg-grey-1 q-pa-md">
          <q-btn unelevated color="positive" label="Done" icon-right="check" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, onBeforeUnmount, computed, watch } from 'vue'
import { useQuasar } from 'quasar'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { useAuthStore } from 'stores/auth-store'
import MenuSelect from 'components/MenuSelect.vue'
import HelpHint from 'components/HelpHint.vue'
import RunnerActionIcon from 'components/RunnerActionIcon.vue'
import { useAsyncGuard } from 'src/composables/useAsyncGuard'
import type {
  BaserunningDecision,
  BaserunningJump,
  DefenseSubReason,
  RunnerAdvanceReason,
} from 'stores/base-types'
import type {
  SequenceItemRole,
  FieldingApproach,
  FieldingExecution,
  FieldingThrowQuality,
  FieldingDecision,
  SequenceItem,
} from 'stores/base-types'

const emit = defineEmits(['switch-view'])
const $q = useQuasar()
const gameStore = useGameStore()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()

// --- EXPOSED METHOD FOR GAME TRACKER ---
const syncTogglesToLastPitch = () => {
  // Clear any locked editing states so the UI correctly bounds to the newest (or empty) edge!
  gameStore.editingPitchId = null
}
defineExpose({ syncTogglesToLastPitch })

onMounted(() => {
  if (containerRef.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        stageConfig.width = entry.contentRect.width
        stageConfig.height = entry.contentRect.height
      }
    })
    resizeObserver.observe(containerRef.value)
  }
  quickLogMode.value = authStore.currentUser?.settings?.enablePitchQuickLog
    ? !authStore.currentUser?.settings?.enablePitchQuickLog
    : false
})

onBeforeUnmount(() => {
  if (resizeObserver && containerRef.value) resizeObserver.unobserve(containerRef.value)
})

const advancedDrawerOpen = ref(false)
const quickLogMode = ref(false)
// Determine if the at-bat has concluded (Walk, Strikeout, or Ball in Play)
const isAtBatConcluded = computed(() => gameStore.currentAbState(true).status !== null)

// --- RESPONSIVE CANVAS SETUP ---
const containerRef = ref<HTMLElement | null>(null)
const stageConfig = reactive({ width: 0, height: 0 })
let resizeObserver: ResizeObserver | null = null

// 3. PERSISTENT PERSPECTIVE TOGGLE
// This writable computed property seamlessly gets/sets the value from the user's settings profile
const isPitcherPerspective = computed({
  get: () => authStore.currentUser?.settings?.usePitcherPerspective ?? false,
  set: (val: boolean) => {
    // Fire and forget the update to the auth store
    void authStore.updateSettings({ usePitcherPerspective: val })
  },
})

// --- AUTOMATIC WALK / HBP HANDLING ---
watch(
  () => gameStore.currentAbState(true).status,
  async (newStatus) => {
    const currentPA = gameStore.viewedPlateAppearance
    if (!currentPA) return

    // 1. Grab the most recent pitch
    const pitches = gameStore.currentAtBatTransactions
    const lastPitch = pitches[pitches.length - 1]

    // 2. CLEAR outcome if the status drops (e.g., user changed a Strike 3 to a Ball)
    if (!newStatus) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (lastPitch && (lastPitch as any).outcome) {
        await gameStore.updatePitch(lastPitch.id, { outcome: null })
      }
      return
    }

    // 3. AUTO-STAMP the terminal outcome onto the final pitch
    if (['BB', 'IBB', 'HP', 'K', '-K', 'D3'].includes(newStatus)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (lastPitch && (lastPitch as any).outcome !== newStatus) {
        await gameStore.updatePitch(lastPitch.id, { outcome: newStatus })
      }
    }

    // If the status resolves to a Walk, Intentional Walk, or Hit By Pitch
    if (newStatus && ['BB', 'IBB', 'HP'].includes(newStatus)) {
      // Check if a runner transaction already exists for this batter to prevent duplicates
      const existingAdvance = gameStore.transactions.find(
        (t) =>
          t.eventType === 'RUNNER_ADVANCE' &&
          t.plateAppearanceId === currentPA.id &&
          t.runnerId === currentPA.batterId,
      )

      if (!existingAdvance) {
        const eventName = newStatus === 'HP' ? 'Hit by Pitch' : 'Walk'

        // NEW: Intercept the auto-advance with a confirmation dialog!
        $q.dialog({
          title: `${eventName} Recorded`,
          message: `Confirm to advance the batter, or Cancel to edit the pitch.`,
          color: 'primary',
          cancel: true,
          persistent: true, // Forces the user to make a choice
        })
          .onOk(() => {
            // User confirmed: Auto-generate the transaction awarding 1st base
            gameStore
              .syncBatterAdvanceFromPitch(newStatus)
              .then(() => {
                $q.notify({
                  message: `${eventName} confirmed. Batter awarded 1st base.`,
                  color: 'positive',
                  icon: 'directions_run',
                  position: 'top',
                  timeout: 2000,
                })
                // Smart routing: If there are existing runners on base, route to the Action view
                const br = gameStore.currentBaseRunners
                const hasRunners = br['1'].length > 0 || br['2'].length > 0 || br['3'].length > 0

                if (hasRunners) {
                  emit('switch-view', 'play')
                }
              })
              .catch(() => {
                console.log('error')
              })
          })
          .onCancel(() => {
            // User canceled: Do nothing!
            // The pitch remains selected on the screen so they can hit "Strike" or "Undo".
          })
      }
    }
  },
)

const otherLabel = computed(() => {
  const advOption = settingsStore._rawPitchAdvancedOptions.find(
    (s) => s.value === (pitchOutcome.value || ''),
  )
  return advOption ? advOption.label : 'Other Pitch Options'
})

// --- ASYNC GUARDS ---
const { isPending: isTappingGrid, guard: guardGridTap } = useAsyncGuard()
const { isPending: isUndoing, guard: guardUndo } = useAsyncGuard()

// Detect whether undoing this pitch would also cascade-delete a linked play/runner data
const undoHasLinkedPlay = computed(() => {
  if (!activeEditPitch.value) return false
  return gameStore.transactions.some(
    (t) =>
      (t.eventType === 'PLAY' || t.eventType === 'RUNNER_ADVANCE') &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (t as any).linkedPitchId === activeEditPitch.value!.id,
  )
})
// NEW: Detect if the absolute most recent event in the At-Bat was a runner advance
const undoIsRunnerAdvance = computed(() => {
  // If the user actively clicked a specific older pitch to edit, do not intercept
  if (gameStore.editingPitchId !== null) return false

  const currentPA = gameStore.viewedPlateAppearance
  if (!currentPA) return false

  const paTxns = gameStore.transactions
    .filter((t) => t.plateAppearanceId === currentPA.id)
    .sort((a, b) => a.sequence - b.sequence)

  const lastTxn = paTxns[paTxns.length - 1]
  return lastTxn?.eventType === 'RUNNER_ADVANCE'
})

// --- STRIKE ZONE GEOMETRY ---
const strikeZoneConfig = computed(() => {
  const w = stageConfig.width * 0.6
  const h = stageConfig.height * 0.5
  return {
    x: (stageConfig.width - w) / 2,
    y: (stageConfig.height - h) / 2 - 20,
    width: w,
    height: h,
    stroke: '#333',
    strokeWidth: 3,
  }
})

const gridLineH1 = computed(() => ({
  points: [
    strikeZoneConfig.value.x,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height / 3,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height / 3,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))

const gridLineH2 = computed(() => ({
  points: [
    strikeZoneConfig.value.x,
    strikeZoneConfig.value.y + (strikeZoneConfig.value.height / 3) * 2,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width,
    strikeZoneConfig.value.y + (strikeZoneConfig.value.height / 3) * 2,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))

const gridLineV1 = computed(() => ({
  points: [
    strikeZoneConfig.value.x + strikeZoneConfig.value.width / 3,
    strikeZoneConfig.value.y,
    strikeZoneConfig.value.x + strikeZoneConfig.value.width / 3,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))

const gridLineV2 = computed(() => ({
  points: [
    strikeZoneConfig.value.x + (strikeZoneConfig.value.width / 3) * 2,
    strikeZoneConfig.value.y,
    strikeZoneConfig.value.x + (strikeZoneConfig.value.width / 3) * 2,
    strikeZoneConfig.value.y + strikeZoneConfig.value.height,
  ],
  stroke: '#666',
  strokeWidth: 1,
}))

// --- BATTER HANDEDNESS INDICATORS ---
const currentBatterBats = computed(() => {
  const batter = gameStore.currentBatter?.player
  if (batter?.bats) return batter.bats
  return 'R'
})

const leftBatterBoxConfig = computed(() => {
  const sz = strikeZoneConfig.value
  const isActive = isPitcherPerspective.value
    ? currentBatterBats.value === 'L' || currentBatterBats.value === 'S'
    : currentBatterBats.value === 'R' || currentBatterBats.value === 'S'

  return {
    x: sz.x - 35,
    y: sz.y + sz.height / 2 - 15,
    width: 30,
    height: 30,
    text: isPitcherPerspective.value ? 'L' : 'R',
    fontSize: 28,
    fontFamily: 'Arial',
    fontStyle: 'bold',
    fill: isActive ? '#2c3e50' : '#bdc3c7',
    opacity: isActive ? 1 : 0.4,
    align: 'center',
    verticalAlign: 'middle',
  }
})

const rightBatterBoxConfig = computed(() => {
  const sz = strikeZoneConfig.value
  const isActive = isPitcherPerspective.value
    ? currentBatterBats.value === 'R' || currentBatterBats.value === 'S'
    : currentBatterBats.value === 'L' || currentBatterBats.value === 'S'

  return {
    x: sz.x + sz.width + 5,
    y: sz.y + sz.height / 2 - 15,
    width: 30,
    height: 30,
    text: isPitcherPerspective.value ? 'R' : 'L',
    fontSize: 28,
    fontFamily: 'Arial',
    fontStyle: 'bold',
    fill: isActive ? '#2c3e50' : '#bdc3c7',
    opacity: isActive ? 1 : 0.4,
    align: 'center',
    verticalAlign: 'middle',
  }
})

// --- ACTIVE PITCH TRACKING ---
const activeEditPitch = computed(() => {
  const currentTxns = gameStore.currentAtBatTransactions || []
  const pitch = currentTxns.find((p) => p.id === gameStore.editingPitchId)
  return pitch ? pitch : currentTxns[currentTxns.length - 1]
})

const isSelected = (id: string | number) => {
  return activeEditPitch.value?.id === id
}

const selectPitch = (id: string | number) => {
  gameStore.editingPitchId = id
}

// Compute graphic elements based on normalized relative coordinates
const plottedPitches = computed(() => {
  const currentTxns = gameStore.currentAtBatTransactions || []
  return currentTxns
    .map((t, index) => ({ t, number: index + 1 }))
    .filter(({ t }) => t.locationTracked) // boolean check, not pitchX/pitchY comparison
    .map(({ t, number }) => {
      const sz = strikeZoneConfig.value
      const displayX = isPitcherPerspective.value ? 1 - t.pitchX : t.pitchX
      return {
        id: t.id,
        number,
        x: sz.x + displayX * sz.width,
        y: sz.y + t.pitchY * sz.height,
        color: settingsStore.pitchResultColors[t.pitchOutcome] || '#607D8B',
      }
    })
})
const untrackedPitches = computed(() => {
  const currentTxns = gameStore.currentAtBatTransactions || []
  return currentTxns
    .map((t, index) => ({ t, number: index + 1 }))
    .filter(({ t }) => !t.locationTracked)
    .map(({ t, number }) => ({
      id: t.id,
      number,
      outcome: t.pitchOutcome,
      color: settingsStore.pitchResultColors[t.pitchOutcome] || '#607D8B',
    }))
})
// --- COLOR MAPPING FOR UNTRACKED PITCHES ---
const getChipColor = (outcome: string, selected: boolean) => {
  // BALLS (Positive / Green)
  if (['B', 'W', 'H', 'I', 'V', 'P'].includes(outcome)) {
    return selected ? 'positive' : 'green-2'
  }
  // IN PLAY (Info / Blue)
  if (outcome === 'X') {
    return selected ? 'info' : 'blue-2'
  }
  // STRIKES & FOULS (Negative / Red)
  if (['C', 'S', 'F', 'T', 'ST', 'Q', 'O', 'R', 'A'].includes(outcome)) {
    return selected ? 'negative' : 'red-2'
  }
  // DEFAULT / OTHER
  return selected ? 'grey-8' : 'grey-3'
}

const getChipTextColor = (outcome: string, selected: boolean) => {
  // Always white text when selected for high contrast
  if (selected) return 'white'

  // Dark text matching the pastel backgrounds when unselected
  if (['B', 'W', 'H', 'I', 'V', 'P'].includes(outcome)) return 'green-10'
  if (outcome === 'X') return 'blue-10'
  if (['C', 'S', 'F', 'T', 'K', 'Q', 'O', 'R', 'A'].includes(outcome)) return 'red-10'

  return 'black'
}
// --- QUICK LOG (NO LOCATION) ---
const { isPending: isQuickLogging, guard: guardQuickLog } = useAsyncGuard()
const handleQuickLog = guardQuickLog(async (outcome: string) => {
  if (isAtBatConcluded.value) {
    $q.notify({
      message: 'At-bat concluded. Please advance to the next batter or Undo the last pitch.',
      color: 'warning',
      icon: 'block',
      position: 'top',
      timeout: 2000,
    })
    return
  }
  const defaultType = activeEditPitch.value ? activeEditPitch.value.pitchType : 'FASTBALL'
  await gameStore.logPitch({ pitchOutcome: outcome, pitchType: defaultType })
  gameStore.editingPitchId = null
  if (outcome === 'X') emit('switch-view', 'hit')
})

// --- ROSTER QUICK-EDIT STATE ---
const editUniformNumber = ref<number | null>(null)
const editBats = ref<'L' | 'R' | 'S'>('R')
const showEditUniform = ref(true)
const { guard: guardSaveRoster } = useAsyncGuard()
// Sync the visible UI whenever the batter changes
watch(
  () => gameStore.viewedPlateAppearance?.id,
  () => {
    const pa = gameStore.viewedPlateAppearance
    const entry = gameStore.currentBatter // FIX: Use currentBatter (handles subs safely)
    if (!pa || !pa.batterId || !entry) return

    // Keep the inputs natively synced to the active batter
    editUniformNumber.value = entry.player.uniformNumber ?? null
    editBats.value = entry.player.bats ?? 'R'
  },
  { immediate: true },
)
// Helper to satisfy TypeScript when selecting input text
const handleFocus = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target && typeof target.select === 'function') {
    target.select()
  }
}
// Auto-save function triggered by @blur and @update:model-value
const autoSaveRoster = guardSaveRoster(async () => {
  if (editUniformNumber.value == null) return

  // Only save if the values actually changed to prevent spamming the DB
  const entry = gameStore.currentBatterEntry
  if (
    entry?.player.uniformNumber === editUniformNumber.value &&
    entry?.player.bats === editBats.value
  ) {
    return
  }

  await gameStore.updateCurrentBatterDetails(editUniformNumber.value, editBats.value)
  $q.notify({ message: 'Batter updated', color: 'positive', position: 'top', timeout: 1000 })
})
// NEW: Force the save to execute BEFORE Vue destroys the input element!
const toggleEditMode = async () => {
  if (showEditUniform.value) {
    await autoSaveRoster()
  }
  showEditUniform.value = !showEditUniform.value
}

const nextBatterPreview = computed(() => {
  const lineup = gameStore.activeOffensiveLineup
  const current = gameStore.currentBatter
  if (!current || lineup.length === 0) return null

  let nextOrderIndex = current.battingOrder + 1
  if (nextOrderIndex > lineup.length) nextOrderIndex = 1

  return lineup.find((e) => e.battingOrder === nextOrderIndex) ?? null
})

// --- UI STATE MAPPING ---
const pitchOutcome = computed(() => activeEditPitch.value?.pitchOutcome || null)
const pitchType = computed(() => activeEditPitch.value?.pitchType || 'FASTBALL')
const pitchQuality = computed(() => activeEditPitch.value?.qualityRating || null)

const handlePitchQuality = async (rating: number) => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, { qualityRating: rating })
  }
}

const primaryCategory = computed(() => {
  const val = pitchOutcome.value
  if (!val) return null
  return settingsStore.getPitchPrimaryCategory(val)
})

// --- EVENT HANDLERS ---
const handlePrimaryCategory = async (cat: string) => {
  let pitchIdToUpdate = null
  if (activeEditPitch.value && cat !== '') {
    pitchIdToUpdate = activeEditPitch.value.id
    await gameStore.updatePitch(pitchIdToUpdate, { pitchOutcome: cat })
  }
  // WIZARD AUTO-ADVANCE: Send them to fielding grid!
  if (cat === 'X') {
    emit('switch-view', 'hit')
  }
}

const handleOutcome = async (outcome: string) => {
  if (!activeEditPitch.value) {
    await gameStore.logPitch({
      pitchX: 0.5,
      pitchY: 0.5,
      pitchOutcome: outcome,
      pitchType: 'FASTBALL',
    })
  } else {
    await gameStore.updatePitch(activeEditPitch.value.id, { pitchOutcome: outcome })
  }
}

const handleAdvancedOption = async (val: string) => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, { pitchOutcome: val })
  } else {
    await gameStore.logPitch({ pitchX: 0.5, pitchY: 0.5, pitchOutcome: val, pitchType: 'FASTBALL' })
  }
  advancedDrawerOpen.value = false

  if (val === 'X' || val === 'Y') {
    emit('switch-view', 'hit')
  } else if (val === 'D3') {
    emit('switch-view', 'play')
  }
}

const handlePitchType = async (type: string) => {
  if (activeEditPitch.value) {
    await gameStore.updatePitch(activeEditPitch.value.id, { pitchType: type })
  }
}

const handleUndo = guardUndo(async () => {
  // INTERCEPT: If the last action was a steal/pickoff, undo that first!
  if (undoIsRunnerAdvance.value) {
    const currentPA = gameStore.viewedPlateAppearance
    const paTxns = gameStore.transactions
      .filter((t) => t.plateAppearanceId === currentPA?.id)
      .sort((a, b) => a.sequence - b.sequence)

    const lastTxn = paTxns[paTxns.length - 1]
    if (lastTxn) {
      await gameStore.deleteRunnerAdvance(lastTxn.id)
      return
    }
  }

  // FALLBACK: Standard pitch deletion
  if (activeEditPitch.value) {
    await gameStore.deletePitch(activeEditPitch.value.id)
    gameStore.editingPitchId = null
  }
})

// --- INTERACTIVE CANVAS HANDLERS ---
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleGridTap = guardGridTap(async (e: any) => {
  if (e.target.className === 'Circle' || e.target.className === 'Text') return

  if (e.evt && e.evt.preventDefault) e.evt.preventDefault()

  // GUARD: Do not allow adding new pitches if the At-Bat has resolved
  if (isAtBatConcluded.value) {
    $q.notify({
      message: 'At-bat concluded. Please advance to the next batter or Undo the last pitch.',
      color: 'warning',
      icon: 'block',
      position: 'top',
      timeout: 2000,
    })
    return
  }

  const stage = e.target.getStage()
  if (!stage) return

  const pointerPosition = stage.getPointerPosition()
  if (!pointerPosition) return

  const sz = strikeZoneConfig.value
  const relX = parseFloat(((pointerPosition.x - sz.x) / sz.width).toFixed(4))
  const relY = parseFloat(((pointerPosition.y - sz.y) / sz.height).toFixed(4))

  const storageX = isPitcherPerspective.value ? parseFloat((1 - relX).toFixed(4)) : relX

  const defaultType = activeEditPitch.value ? activeEditPitch.value.pitchType : 'FASTBALL'
  await gameStore.logPitch({
    pitchX: storageX,
    pitchY: relY,
    pitchOutcome: 'B',
    pitchType: defaultType,
  })
  gameStore.editingPitchId = null
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleStageDragEnd = async (e: any) => {
  const id = e.target.id()
  if (!id) return

  const sz = strikeZoneConfig.value
  const relX = parseFloat(((e.target.x() - sz.x) / sz.width).toFixed(4))
  const relY = parseFloat(((e.target.y() - sz.y) / sz.height).toFixed(4))

  // Invert the X input back to standard Umpire POV if Pitcher POV is active
  const storageX = isPitcherPerspective.value ? parseFloat((1 - relX).toFixed(4)) : relX

  await gameStore.updatePitch(id, { pitchX: storageX, pitchY: relY })
  gameStore.editingPitchId = id // Select the pitch we just dragged
}

// --- RUNNER SHEET OPTIONS & SETTERS ---
const reasonOptions = computed(() => settingsStore.primaryRunnerAdvanceReasonsSimplified)
const defenseSubReasonOptions = computed(() => settingsStore.primaryDefenseSubReasons)
const jumpOptions = computed(() => settingsStore.primaryBaserunningJumpOptions)
const decisionOptions_runner = computed(() => settingsStore.primaryBaserunningDecisionOptions)

const selectedRunnerUniform = computed(() => {
  if (!selectedRunnerId.value) return '-'
  const br = gameStore.currentBaseRunners
  // Search all bases for this specific runner ID
  for (const base of ['1', '2', '3', 'B'] as const) {
    const runner = br[base].find((r) => r.id === selectedRunnerId.value)
    if (runner) return runner.uniformNumber || '-'
  }
  return '-'
})

const availableBases = computed(() => {
  if (draftStartBase.value === '1') return ['1', '2', '3', 'H']
  if (draftStartBase.value === '2') return ['2', '3', 'H']
  if (draftStartBase.value === '3') return ['3', 'H']
  return ['1', '2', '3', 'H']
})

const setTargetBase = (base: string) => (draftTargetBase.value = base)
const setResult = (isOut: boolean) => (draftIsOut.value = isOut)

const selectReason = (reason: RunnerAdvanceReason) => {
  draftReason.value = reason
  reasonTouched.value = true
  if (reason !== 'DEFENSE') draftDefenseSubReason.value = ''
}
const selectDefenseSubReason = (sub: DefenseSubReason) => (draftDefenseSubReason.value = sub)
const selectJump = (jump: BaserunningJump) => (draftJump.value = jump)
const selectDecision = (decision: BaserunningDecision) => (draftDecision.value = decision)

const showRunnerActionSheet = ref(false)
const runnerActionType = ref<'Pickoff' | 'Steal' | null>(null)
const targetRunnerId = ref<number | null>(null)
const draftInitiator = ref<number>(1)
const draftReceiver = ref<number>(3)

// Dynamically list all runners currently on base
const activeRunners = computed(() => {
  const br = gameStore.currentBaseRunners
  const currentBatterId = gameStore.currentBatter?.player.id // FIX: Handle subs here too!

  if (!br) return []
  const runners = []

  // Explicitly check bases and filter out the batter at the plate
  if (br['3'].length && br['3'][0]!.id !== currentBatterId) {
    runners.push({ label: `Runner on 3B`, value: br['3'][0]!.id })
  }
  if (br['2'].length && br['2'][0]!.id !== currentBatterId) {
    runners.push({ label: `Runner on 2B`, value: br['2'][0]!.id })
  }
  if (br['1'].length && br['1'][0]!.id !== currentBatterId) {
    runners.push({ label: `Runner on 1B`, value: br['1'][0]!.id })
  }
  return runners
})

// Check if any base currently has a runner (used for UI visibility)
const hasRunners = computed(() => activeRunners.value.length > 0)

// 1. OPEN THE INITIAL SETUP SHEET
const openActionSheet = (type: 'Pickoff' | 'Steal') => {
  runnerActionType.value = type

  // Set smart defaults
  if (type === 'Pickoff') {
    draftInitiator.value = 1 // Pitcher
    draftReceiver.value = 3 // Default to 1B
  } else {
    draftInitiator.value = 2 // Catcher
    draftReceiver.value = 4 // Default to 2B
  }

  // Auto-select the runner if there is only one on base
  if (activeRunners.value.length === 1) {
    targetRunnerId.value = activeRunners.value[0]!.value
  } else {
    targetRunnerId.value = null // Force coach to choose
  }

  showRunnerActionSheet.value = true
}

const draftReason = ref<RunnerAdvanceReason | ''>('OTHER')
const reasonTouched = ref(false)
const selectedRunnerId = ref<number | null>(null)
const showRunnerSheet = ref(false)
const isDraftingRunnerSequence = ref(false)

const draftStartBase = ref<string>('B')
const draftTargetBase = ref<string | null>(null)
const draftIsOut = ref<boolean | null>(null)
const draftDefenseSubReason = ref<DefenseSubReason | ''>('')
const draftJump = ref<BaserunningJump | ''>('')
const draftDecision = ref<BaserunningDecision>('not_required')

// 2. BUILD SEQUENCE AND HAND-OFF TO RUNNER SHEET
const buildSequenceAndContinue = async () => {
  if (!targetRunnerId.value) return

  const startBase = getRunnerBase(targetRunnerId.value) || '1'
  const rTxn = await gameStore.getOrCreateActiveRunnerAdvance(targetRunnerId.value, startBase)

  if (rTxn) {
    await gameStore.addFielderToTransaction(rTxn.id, draftInitiator.value, 1)
    await gameStore.addFielderToTransaction(rTxn.id, draftReceiver.value, 1)

    showRunnerActionSheet.value = false
    openRunnerSheet(targetRunnerId.value)

    // Ensure we use the proper enums!
    draftReason.value = runnerActionType.value === 'Pickoff' ? 'PICKOFF' : 'STOLEN_BASE'
    reasonTouched.value = true
  }
}

const getRunnerBase = (id: number) => {
  const br = gameStore.currentBaseRunners
  if (br['1'].some((r) => r.id === id)) return '1'
  if (br['2'].some((r) => r.id === id)) return '2'
  if (br['3'].some((r) => r.id === id)) return '3'
  //if (br['B'].some((r) => r.id === id)) return 'B'
  return null
}
// Dynamically target either the Batter's Play or the active Runner's Advance!
const activeTxnId = computed(() => {
  if (selectedRunnerId.value) {
    const currentPA = gameStore.viewedPlateAppearance
    const rTxns = gameStore.transactions.filter(
      (t) =>
        t.eventType === 'RUNNER_ADVANCE' &&
        t.runnerId === selectedRunnerId.value &&
        t.plateAppearanceId === currentPA?.id,
    )

    // FIX: If we are in the Pickoff/Steal workflow, grab the most recent transaction
    // even if it is no longer incomplete, so the Fielder Sheet can still edit it!
    if (runnerActionType.value && rTxns.length > 0) {
      return rTxns[rTxns.length - 1]?.id
    }

    // Otherwise, strictly look for an incomplete transaction
    return rTxns.find((t) => t.isIncomplete)?.id
  } else {
    return gameStore.activePlay?.id
  }
})

const openRunnerSheet = (id: number, prefilledBase: string | null = null) => {
  selectedRunnerId.value = id
  draftStartBase.value = getRunnerBase(id) || '1'
  // await gameStore.getOrCreateActiveRunnerAdvance(id, draftStartBase.value as any)
  draftTargetBase.value = prefilledBase
  draftIsOut.value = null
  draftReason.value = 'OTHER'
  draftDefenseSubReason.value = ''
  draftJump.value = ''
  draftDecision.value = 'not_required'
  showRunnerSheet.value = true
  reasonTouched.value = false
}
const { isPending: isSavingRunner, guard: guardSaveRunner } = useAsyncGuard()
const saveRunner = guardSaveRunner(async () => {
  if (!selectedRunnerId.value || !draftTargetBase.value || !draftReason.value) return

  let targetId = activeTxnId.value
  if (!targetId) {
    const rTxn = await gameStore.getOrCreateActiveRunnerAdvance(selectedRunnerId.value)
    targetId = rTxn?.id
  }

  if (targetId) {
    await gameStore.finalizeRunnerAdvance(targetId, {
      endBase: draftTargetBase.value,
      isOut: draftIsOut.value,
      runsScored: !draftIsOut.value && draftTargetBase.value === 'H' ? 1 : 0,
      advanceReason: draftReason.value,
      defenseSubReason:
        draftReason.value === 'DEFENSE' ? draftDefenseSubReason.value || null : null,
      baserunningJump: draftJump.value || undefined,
      baserunningDecision: draftDecision.value,
    })
  }

  showRunnerSheet.value = false

  // NEW: If this is part of the Pickoff/Steal workflow, open the Fielder Sheet directly!
  if (runnerActionType.value && activeSequence.value.length > 0) {
    openSequenceEdit(0)
    return // Exit early so we don't clear the selectedRunnerId yet
  }

  // Standard manual cleanup
  selectedRunnerId.value = null
  isDraftingRunnerSequence.value = false
})

// --- FIELDER DIMENSIONS (MECHANICS & IQ) STATE ---
const showEditFielderSheet = ref(false)
const editSequenceIndex = ref<number | null>(null)

const editRoleState = ref<SequenceItemRole>('fielded')
const editApproachState = ref<FieldingApproach>('routine')
const editExecutionState = ref<FieldingExecution>('clean')
const editThrowState = ref<FieldingThrowQuality>('not_required')
const editDecisionState = ref<FieldingDecision>('not_required')
const editDifficultyState = ref<number | null>(null)
const editErrorState = ref<'none' | 'fielding' | 'throwing'>('none')

const activeSequence = computed(() => {
  const txn = gameStore.transactions.find((t) => t.id === activeTxnId.value)
  // @ts-expect-error - playSequence exists on RunnerAdvance
  return txn?.playSequence || []
})

// Options computed directly from settings
const approachOptions = computed(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  return item ? settingsStore.approachOptionsForPosition(item.position, item.role || 'fielded') : []
})
const executionOptions = computed(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  return item
    ? settingsStore.executionOptionsForPosition(item.position, item.role || 'fielded')
    : []
})
const throwOptions = computed(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  return item ? settingsStore.throwOptionsForPosition(item.position, item.role || 'fielded') : []
})
const decisionOptions = computed(() => {
  const item = activeSequence.value[editSequenceIndex.value ?? -1]
  return item ? settingsStore.decisionOptionsForPosition(item.position, item.role || 'fielded') : []
})

const approachHidden = computed(() => approachOptions.value.length === 0)
const executionHidden = computed(() => executionOptions.value.length === 0)
const throwHidden = computed(() => throwOptions.value.length === 0)
const decisionHidden = computed(() => decisionOptions.value.length === 0)

const getPositionName = (pos: number) => settingsStore.positionStrings[pos] || String(pos)

const editItemName = computed(() => {
  if (editSequenceIndex.value === null) return ''
  const pos = activeSequence.value[editSequenceIndex.value]?.position || -1
  return getPositionName(pos)
})

const editItemUniform = computed(() => {
  if (editSequenceIndex.value === null) return ''
  const pos = activeSequence.value[editSequenceIndex.value]?.position
  const snapshotFielder = gameStore.currentDefence.find((f) => f.position === pos)
  return snapshotFielder?.uniformNumber ?? '-'
})

const openSequenceEdit = (index: number) => {
  editSequenceIndex.value = index
  const item = activeSequence.value[index]
  if (item) {
    editRoleState.value = item.role || 'fielded'
    editApproachState.value = approachHidden.value ? 'not_scored' : item.approach || 'routine'
    editExecutionState.value = executionHidden.value ? 'not_scored' : item.execution || 'clean'
    editThrowState.value = throwHidden.value ? 'not_required' : item.throw || 'not_required'
    editDecisionState.value = decisionHidden.value
      ? 'not_required'
      : item.decision || 'not_required'
    editErrorState.value = item.isError ? item.errorType || 'fielding' : 'none'
    editDifficultyState.value = item.difficulty ?? 1
  }
  showEditFielderSheet.value = true
}

const onEditChange = async () => {
  if (editSequenceIndex.value !== null && activeTxnId.value) {
    const updates: Partial<SequenceItem> = {
      role: editRoleState.value,
      approach: editApproachState.value,
      execution: editExecutionState.value,
      throw: editThrowState.value,
      decision: editDecisionState.value,
      isError: editErrorState.value !== 'none',
      errorType: editErrorState.value !== 'none' ? editErrorState.value : null,
    }
    await gameStore.updateSequenceItem(activeTxnId.value, editSequenceIndex.value, updates)
  }
}

const onEditHandleApproach = async (val: string) => {
  editApproachState.value = val as FieldingApproach
  await onEditChange()
}
const onEditHandleExecution = async (val: string) => {
  editExecutionState.value = val as FieldingExecution
  await onEditChange()
}
const onEditHandleThrow = async (val: string) => {
  editThrowState.value = val as FieldingThrowQuality
  await onEditChange()
}
const onEditHandleDecision = async (val: string) => {
  editDecisionState.value = val as FieldingDecision
  await onEditChange()
}

const navigateSequence = (direction: number) => {
  if (editSequenceIndex.value === null) return
  const newIndex = editSequenceIndex.value + direction
  if (newIndex >= 0 && newIndex < activeSequence.value.length) openSequenceEdit(newIndex)
}
// --- CONTEXTUAL UI VISIBILITY RULES ---
const computedRoleHidden = computed(() => {
  if (runnerActionType.value === 'Pickoff') return true
  if (runnerActionType.value === 'Steal' && editSequenceIndex.value === 1) return true
  return false
})

const computedDifficultyHidden = computed(() => {
  if (runnerActionType.value === 'Pickoff' && editSequenceIndex.value === 0) return true
  return false
})

const computedApproachHidden = computed(() => {
  // If the option array is naturally empty, keep it hidden
  if (approachOptions.value.length === 0) return true
  if (runnerActionType.value === 'Pickoff' && editSequenceIndex.value === 0) return true
  return false
})

const computedExecutionHidden = computed(() => {
  if (executionOptions.value.length === 0) return true
  if (runnerActionType.value === 'Pickoff' && editSequenceIndex.value === 0) return true
  return false
})

const computedThrowHidden = computed(() => {
  if (throwOptions.value.length === 0) return true
  // The receiver (2nd fielder) doesn't throw the ball in a standard pickoff/steal
  if (runnerActionType.value && editSequenceIndex.value === 1) return true
  return false
})

// Clear the runner state when the Fielder Sheet fully closes
const onFielderSheetHide = () => {
  // FIX: Only wipe the runner state if the main Runner Sheet is NOT open beneath it!
  if (runnerActionType.value && !showRunnerSheet.value) {
    selectedRunnerId.value = null
    runnerActionType.value = null
    isDraftingRunnerSequence.value = false
  }
}
</script>

<style scoped>
.canvas-container {
  border: 3px dashed #999;
  border-radius: 8px;
  margin: 4px;
  box-sizing: border-box;
  min-height: 200px;
  overflow: hidden;
}
</style>
